<template>
  <div>
    <DefaultModal 
      :title="$t('next_status')" 
      ref="modal" 
      @submit="update"
      @modal-closed="close"
    >
      <template v-slot:content>
        <v-form>
          <v-row>
            <v-col cols="12">
              <v-select
                v-model="selectedJobPoolStatuses"
                :items="jobPoolStatuses"
                :label="$t('select_job_pool_status')"
                :item-text="item => getJobPoolStatusTitle(item)"
                item-value="job_pool_status_id"
                :menu-props="{ maxHeight: '400' }"  
                multiple
                outlined
                dense
              >
                <template v-slot:selection="{ item, index }">
                  <v-chip small v-if="index === 0">
                    <span>{{ getJobPoolStatusTitle(item) }}</span>
                  </v-chip>
                  <span
                    v-if="index === 1"
                    class="grey--text text-caption"
                  >
                    (+{{ selectedJobPoolStatuses.length - 1 }} others)
                  </span>
                </template>
            </v-select>
            </v-col>

            <v-col v-if="loading">
              <v-progress-linear
                indeterminate
              ></v-progress-linear>
            </v-col>

            <v-col cols="12">
              <label class="text-body-1">
                {{ $t('job_pool_statuses') }}
              </label>
              <v-simple-table
                height="230"
                class="mt-3 bordered"
              >
                <template v-slot:default>
                  <tbody>
                    <tr
                      v-for="item in selectedJobPoolStatuses"
                      :key="item.name"
                    >
                      <td>{{ getJobPoolStatusTitle(item) }}</td>
                      <td class="text-right">
                        <v-btn icon small @click="removeJobPoolStatusFromList(item)">
                          <v-icon small>mdi-close</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-col>
          </v-row>
        </v-form>
      </template>
    </DefaultModal>
  </div>
</template>

<style scoped>
.bordered {
  border: thin solid rgba(0, 0, 0, 0.12);
}
</style>

<script>
import { mapActions } from 'vuex'
import DefaultModal from '@/components/DefaultModal.vue'
import JobActions from '@/store/job/actions-types'

export default {
  name: "NextStatusDialog",

  components: {
    DefaultModal,
  },

  props: {
    jobPoolStatuses: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      loading: false,
      sourceJobPoolStatus: null,
      nextStatusesByCurrent: [],
      selectedJobPoolStatuses: []
    }
  },

  methods: {
    ...mapActions('job', [
      JobActions.GET_NEXT_STATUSES_BY_CURRENT,
      JobActions.CREATE_NEXT_STATUS,
      JobActions.DELETE_NEXT_STATUS,
    ]),

    clearData() {
      this.selectedJobPoolStatuses = []
    },

    open(jobPoolStatus) {
      this.sourceJobPoolStatus = jobPoolStatus
      this.clearData()
      this.$refs.modal.openModal()
      this.fetchData()
    },

    close() {
      this.clearData()
      this.$refs.modal.closeModal()
    },

    getJobPoolStatusTitle(item) {
      const jps =
        typeof item === 'object'
          ? item
          : this.jobPoolStatuses.find(j => j.job_pool_status_id == item)

      return jps ? `${jps.pool_title} - ${jps.status_title}` : '—'
    },

    removeJobPoolStatusFromList(item) {
      const index = this.selectedJobPoolStatuses.indexOf(item)
      if (index > -1) {
        this.selectedJobPoolStatuses.splice(index, 1)
      }
    },    

    update() {
      this.$refs.modal.errorMessage = null
      this.$refs.modal.errorList = []

      const promisesToCreate = this.save()
      const promisesToDelete = this.delete()

      Promise.all([...promisesToCreate, ...promisesToDelete])
        .then(() => {
          const message = 'next_status_updated'
          this.$emit('saved', message)
          this.close()
        })
        .catch((error) => {
          if (error.response && error.response.data) {
            this.$refs.modal.errorMessage = error.response.data.message || 'An error occurred'
          }
        })
        .finally(() => {
          this.$refs.modal.submitting = false
        })
    },

    save() {
      const existingNextStatusIds = this.nextStatusesByCurrent.map(item => item.next_job_pool_status_id)
      const idsToCreate = this.selectedJobPoolStatuses.filter(id => !existingNextStatusIds.includes(id))

      const promisesToCreate = []

      idsToCreate.forEach(nextStatusId => {
        const data = {
          source_job_pool_status_id: this.sourceJobPoolStatus.job_pool_status_id,
          next_job_pool_status_id: nextStatusId
        }
        promisesToCreate.push(this[JobActions.CREATE_NEXT_STATUS](data))
      })

      return promisesToCreate
    },

    delete() {
      const idsToDelete = this.nextStatusesByCurrent
        .filter(item => !this.selectedJobPoolStatuses.includes(item.next_job_pool_status_id))
        .map(item => item.next_job_pool_status_id)

      const promisesToDelete = []

      idsToDelete.forEach(nextStatusId => {
        const params = {
          source_job_pool_status_id: this.sourceJobPoolStatus.job_pool_status_id,
          next_job_pool_status_id: nextStatusId
        }
        promisesToDelete.push(this[JobActions.DELETE_NEXT_STATUS](params))
      })

      return promisesToDelete
    },

    fetchData() {
      this.loading = true
      this.clearData()

      const id = this.sourceJobPoolStatus.job_pool_status_id

      this[JobActions.GET_NEXT_STATUSES_BY_CURRENT]({ id })
        .then(result => {
          this.nextStatusesByCurrent = result.items || []
          this.selectedJobPoolStatuses = this.nextStatusesByCurrent.map(item => item.next_job_pool_status_id)
        })
        .catch((error) => {
          if (error.response && error.response.data) {
            this.$refs.modal.errorMessage = error.response.data.message || 'An error occurred'
          }
        })
        .finally(() => {
          this.loading = false
        })
    }

  }
}
</script>
